import { useCallback, useEffect, useState } from "react";
import { useAuth } from "@/core/hooks/useAuth";
import {
  PROGRESSO_VAZIO,
  chaveProgresso,
  dispensarCartao,
  dispensarCartaoDaLicao,
  gravarProgresso,
  lerProgresso,
  marcarBoasVindas,
  marcarVista,
  type ProgressoAjuda,
} from "@/core/ajuda/progresso";

/** localStorage pode lançar erro só de ser acessado (janela privativa, dados bloqueados). */
function obterStorage(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

/**
 * Progresso da Ajuda do Elos neste aparelho. Isolado de propósito: é o único
 * ponto a trocar se o progresso um dia passar a morar na conta.
 */
export function useProgressoAjuda() {
  const { pessoa } = useAuth();
  const pessoaId = pessoa?.id ?? null;
  // `de` diz de quem é o progresso guardado aqui. Antes de ler o aparelho, `pronto` é falso: sem isso a
  // primeira renderização usaria o progresso vazio e a boas-vindas/o cartão piscariam para quem já os fechou.
  const [estado, setEstado] = useState<{ de: string | null; progresso: ProgressoAjuda }>({
    de: null,
    progresso: PROGRESSO_VAZIO,
  });

  useEffect(() => {
    setEstado(
      pessoaId
        ? { de: pessoaId, progresso: lerProgresso(obterStorage(), chaveProgresso(pessoaId)) }
        : { de: null, progresso: PROGRESSO_VAZIO },
    );
  }, [pessoaId]);

  const atualizar = useCallback(
    (alterar: (atual: ProgressoAjuda) => ProgressoAjuda) => {
      if (!pessoaId) return;
      const chave = chaveProgresso(pessoaId);
      // Relê antes de alterar: outra aba pode ter mudado o progresso.
      const novo = alterar(lerProgresso(obterStorage(), chave));
      gravarProgresso(obterStorage(), chave, novo);
      setEstado({ de: pessoaId, progresso: novo });
    },
    [pessoaId],
  );

  const concluirLicao = useCallback(
    (licaoId: string) => atualizar((p) => marcarVista(p, licaoId)),
    [atualizar],
  );
  const dispensarConvite = useCallback(() => atualizar(dispensarCartao), [atualizar]);
  const concluirBoasVindas = useCallback(() => atualizar(marcarBoasVindas), [atualizar]);
  const dispensarCartaoLicao = useCallback(
    (licaoId: string) => atualizar((p) => dispensarCartaoDaLicao(p, licaoId)),
    [atualizar],
  );

  return {
    progresso: estado.progresso,
    pronto: pessoaId !== null && estado.de === pessoaId,
    concluirLicao,
    dispensarConvite,
    dispensarCartaoLicao,
    concluirBoasVindas,
  };
}
